import { forwardRef, useEffect, useId, useImperativeHandle, useRef } from 'react';
import { Check, Minus } from 'lucide-react';
import type { CheckboxProps } from './Checkbox.types';
import './Checkbox.scss';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate = false, error, id, className, disabled, checked, ...rest },
  ref,
) {
  const autoId = useId();
  const cbId = id || `checkbox-${autoId}`;
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement, []);

  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      htmlFor={cbId}
      className={[
        'checkbox',
        disabled ? 'checkbox--disabled' : '',
        error ? 'checkbox--error' : '',
        className || '',
      ].filter(Boolean).join(' ')}
    >
      <span className="checkbox__control">
        <input
          ref={inner}
          id={cbId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${cbId}-error` : undefined}
          className="checkbox__native"
          {...rest}
        />
        <span className="checkbox__box" aria-hidden="true">
          {indeterminate ? <Minus size={14} /> : checked ? <Check size={14} /> : null}
        </span>
      </span>
      {label || description ? (
        <span className="checkbox__text">
          {label ? <span className="checkbox__label">{label}</span> : null}
          {description ? <span className="checkbox__description">{description}</span> : null}
          {error ? <span id={`${cbId}-error`} className="checkbox__error" role="alert">{error}</span> : null}
        </span>
      ) : null}
    </label>
  );
});

export default Checkbox;
